import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { formatVersion } from '@/utils/releaseVersion';

/**
 * Single source of truth for the desktop app's version.
 *
 * Happy path (build-time injection):
 *   `NEXT_PUBLIC_DESKTOP_APP_VERSION` is sourced from
 *   `src-tauri/tauri.conf.json`'s `version` field. Next.js inlines the
 *   value at compile time via webpack DefinePlugin, so
 *   `getDesktopVersion()` returns it synchronously from the first render.
 *
 *   Two surfaces feed the env var:
 *   - **CI** (`.github/workflows/desktop-app-publish.yml`): an
 *     `Inject desktop app version` step reads the env-specific overlay
 *     (`tauri.conf.staging.json` / `tauri.conf.production.json` — the
 *     source of truth; `web_build` runs in parallel with `draft` so the
 *     base `tauri.conf.json` hasn't been merged yet) and writes
 *     `NEXT_PUBLIC_DESKTOP_APP_VERSION` to `$GITHUB_ENV`. The
 *     `Build web app` step inherits it automatically. No on-disk state.
 *   - **Local desktop build** (`pnpm tauri:build-*` /
 *     `pnpm tauri:dev-staging`): `tauri-config.js` upserts
 *     `NEXT_PUBLIC_DESKTOP_APP_VERSION` into `.env.local` after merging
 *     env overlays, before `tauri build` triggers `next build`.
 *
 * Fallback path (runtime):
 *   For entry points that skip both injection mechanisms (notably
 *   `pnpm tauri:dev`, which goes straight to `tauri dev`),
 *   `initDesktopVersion()` falls back to the async
 *   `@tauri-apps/api/app::getVersion()` call and logs a warning. Events
 *   fired before that resolves will omit the version field — accepted
 *   gap, only ever hit in local dev.
 *
 * Web builds always return undefined regardless of build-time injection
 * (`getDesktopVersion()` short-circuits on `isTauri() === false`).
 */

const BUILD_TIME_VERSION = process.env.NEXT_PUBLIC_DESKTOP_APP_VERSION;
const BUILD_TIME_BUILD_ID = process.env.NEXT_PUBLIC_DESKTOP_BUILD_ID;

let cachedVersion: string | undefined = BUILD_TIME_VERSION;
let fallbackLoad: Promise<void> | undefined;

export const getDesktopVersion = (): string | undefined => {
  if (!isTauri()) return undefined;
  return cachedVersion;
};

/**
 * App version for the `X-Client-Version` HTTP header (APP-8775).
 *
 * - Desktop: Tauri semver from {@link getDesktopVersion} (may be undefined
 *   briefly in local `tauri:dev` before the runtime fallback resolves).
 * - Web: deployment release tag from {@link formatVersion}, matching Datadog
 *   RUM / `public/version.json` (e.g. `1.7.0-a5d41b7`, or `dev-local` locally).
 */
export const getClientVersion = (): string | undefined => {
  if (isTauri()) {
    return getDesktopVersion();
  }
  return formatVersion(
    process.env.NEXT_PUBLIC_RELEASE_REF,
    process.env.NEXT_PUBLIC_RELEASE_SHA
  );
};

/**
 * Short git SHA baked into the bundle by CI (`Inject desktop build ID` in
 * `.github/workflows/desktop-app-publish.yml`). During a publish cycle every
 * release-branch build carries the same semver, so the build ID is what
 * distinguishes one CrabNebula draft from another (APP-5631).
 *
 * Returns undefined on web builds and on local desktop builds (which skip
 * the CI injection) — callers should render the bare version then. No
 * runtime fallback on purpose: unlike the version, the build ID only exists
 * for CI artifacts.
 */
export const getDesktopBuildId = (): string | undefined => {
  if (!isTauri()) return undefined;
  return BUILD_TIME_BUILD_ID || undefined;
};

/**
 * Returns the spread-ready trait object for analytics destinations.
 *
 * - On desktop builds with a resolved version: `{ desktop_version, app_version }`
 *   (both fields, same value).
 * - On web builds, or before the runtime fallback resolves: `{}` — the spread
 *   becomes a no-op so no field is emitted.
 *
 * `desktop_version` is the legacy field that PostHog feature-flag release
 * conditions and existing dashboards depend on. `app_version` (added
 * 2026-05-11 alongside APP-4897) is the cross-platform name that should
 * eventually subsume `desktop_version` once web/mobile populate it;
 * `client_platform` already disambiguates per platform. Both ship in
 * parallel during the migration window.
 */
export const getAppVersionTraits = (): {
  desktop_version?: string;
  app_version?: string;
} => {
  const version = getDesktopVersion();
  if (!version) return {};
  return { desktop_version: version, app_version: version };
};

/**
 * Build-id trait for analytics, mirroring {@link getAppVersionTraits}: returns
 * `{ build_id }` on a CI desktop build, or `{}` (omit) when absent — web, local,
 * or non-publish builds. Use for OBJECT-SPREAD destinations that follow the
 * omit-when-absent convention (per-event `extendedProps`, PostHog super-props).
 *
 * Intentional cross-destination convention (APP-6116): omit-when-absent here
 * matches the other per-event/spread dims (`installer_type`, app-version). The
 * Snowplow `client_context` generator, the Flagsmith trait, and the Datadog
 * global context instead emit `build_id: null` (and `update_channel: null`) for
 * the same absent state — they attach the key ONCE, so a present-but-null key is
 * the right shape there. A desktop event fired pre-resolve is therefore `{}`
 * (key absent) on the spread destinations and `null` on the once-attached ones;
 * that split is deliberate (not a bug) — analysts distinguishing null-vs-absent
 * should treat both as "build id not yet known".
 */
export const getBuildIdTraits = (): { build_id?: string } => {
  const buildId = getDesktopBuildId();
  return buildId ? { build_id: buildId } : {};
};

export const initDesktopVersion = async (): Promise<void> => {
  // Truthy check on purpose: an empty-string injection (e.g. a misconfigured
  // pipeline that wrote `NEXT_PUBLIC_DESKTOP_APP_VERSION=`) should still hit
  // the runtime fallback rather than silently emitting events with no version.
  if (BUILD_TIME_VERSION) return;
  if (!isTauri()) return;

  if (!fallbackLoad) {
    logger.warn(
      '[appVersion] NEXT_PUBLIC_DESKTOP_APP_VERSION was not injected at build ' +
        'time — falling back to @tauri-apps/api/app::getVersion(). Check that ' +
        'next.config.js reads src-tauri/tauri.conf.json correctly.'
    );
    fallbackLoad = import('@tauri-apps/api/app')
      .then(({ getVersion }) => getVersion())
      .then((version) => {
        cachedVersion = version;
      })
      .catch((err) => {
        logger.error('[appVersion] Fallback getVersion() failed:', err);
      });
  }
  await fallbackLoad;
};
